import { useEffect, useState } from "react";
import { Popconfirm, Tooltip, message } from "antd";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import GenericEvidenceForm from "../../components/care/GenericEvidenceForm";

const EVIDENCE_TYPE = "WA_AFH_003_ANNUAL_LICENSE_FEE";

const pageItems = (payload) =>
  Array.isArray(payload)
    ? payload
    : payload?.content || payload?.data || payload?.items || [];

const currentUserId = (user = {}) =>
  user?.id || user?.userId || user?.uid || "";

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `$${Number(value).toFixed(2)}`;
};

const paymentMethodLabel = (value) =>
  ({
    CHECK: "Check",
    ONLINE_DSHS_PORTAL: "Online DSHS portal",
    MONEY_ORDER: "Money order",
    OTHER: "Other",
  })[value] ||
  value ||
  "-";

const statusTone = (record) => {
  if (record.daysUntilExpiration == null) return "medium";
  if (record.daysUntilExpiration < 0) return "high";
  if (!record.isPaid && record.daysUntilExpiration <= 30) return "medium";
  return "low";
};

const statusLabel = (record) => {
  if (record.daysUntilExpiration == null) return "Unknown";
  if (record.daysUntilExpiration < 0) return "Past due";
  if (!record.isPaid && record.daysUntilExpiration <= 30)
    return `Due in ${record.daysUntilExpiration}d`;
  return record.isPaid ? "Paid" : "Not yet due";
};

export default function WorkspaceLicenseFeePage() {
  const { user } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const actorId = currentUserId(user);
  const workspaceParams = {
    wid: selectedWorkspaceId,
    workspaceId: selectedWorkspaceId,
  };

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("CREATE"); // CREATE | EDIT | VIEW
  const [activeRecord, setActiveRecord] = useState(null);
  const [deletingId, setDeletingId] = useState("");

  const loadRecords = () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setRecords([]);
      return;
    }
    setLoading(true);
    setError("");
    careService.workspaceLicenseRecords
      .list(workspaceParams)
      .then((data) => setRecords(pageItems(data)))
      .catch((err) => {
        setRecords([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load license records.",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadRecords, [selectedWorkspaceId, workspaceListReady]);

  const openNew = () => {
    setActiveRecord(null);
    setDrawerMode("CREATE");
    setDrawerOpen(true);
  };

  const openView = (record) => {
    setActiveRecord(record);
    setDrawerMode("VIEW");
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setActiveRecord(record);
    setDrawerMode("EDIT");
    setDrawerOpen(true);
  };

  const handleSaved = () => {
    setDrawerOpen(false);
    setActiveRecord(null);
    loadRecords();
  };

  const deleteRecord = async (record) => {
    setDeletingId(record.id);
    try {
      await careService.workspaceLicenseRecords.delete(record.id, {
        actorId,
        ...workspaceParams,
      });
      setRecords((current) => current.filter((item) => item.id !== record.id));
      message.success("License record deleted.");
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to delete license record.",
      );
    } finally {
      setDeletingId("");
    }
  };

  const current = records[0]; // most recent by expirationDate, per findByWorkspaceIdOrderByExpirationDateDesc
  const metrics = {
    currentExpiration: current ? formatDate(current.expirationDate) : "Not set",
    daysUntilExpiration: current?.daysUntilExpiration ?? null,
    paidStatus: current ? (current.isPaid ? "Paid" : "Unpaid") : "No record",
    totalRecords: records.length,
  };

  return (
    <CareShell className="dashboard-content workspace-license-fee-page emergency-evacuation-drills-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Annual License Fee</h1>
          <p>
            Track the per-bed AFH license fee, payment proof, and renewal
            deadline (RCW 70.128.060, WAC 388-76-10030).
          </p>
        </div>
        <button
          className="mini-btn emergency-evacuation-schedule-button"
          type="button"
          onClick={openNew}
        >
          <i className="ti ti-receipt" /> Add License Record
        </button>
      </div>

      <div className="medication-summary-grid emergency-evacuation-drill-summary-grid">
        <Panel title="Current expiration">
          <div className="resident-stat-value compact">
            {metrics.currentExpiration}
          </div>
        </Panel>
        <Panel title="Days remaining">
          <div
            className={`resident-stat-value${metrics.daysUntilExpiration != null && metrics.daysUntilExpiration < 30 ? " text-danger" : ""}`}
          >
            {metrics.daysUntilExpiration ?? "-"}
          </div>
        </Panel>
        <Panel title="Payment status">
          <div className="resident-stat-value compact">
            {metrics.paidStatus}
          </div>
        </Panel>
        <Panel title="Total records">
          <div className="resident-stat-value">{metrics.totalRecords}</div>
        </Panel>
      </div>

      <Panel title="License Fee History">
        {error ? <p className="form-error">{error}</p> : null}
        <div className="medication-table-wrap">
          <table className="medication-table">
            <thead>
              <tr>
                <th>Expiration</th>
                <th>Fee</th>
                <th>Paid</th>
                <th>Method</th>
                <th>Confirmation</th>
                <th>Status</th>
                <th style={{textAlign:"right"}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    Loading license records...
                  </td>
                </tr>
              ) : null}
              {!loading && !records.length ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    No license records logged yet.
                  </td>
                </tr>
              ) : null}
              {!loading &&
                records.map((record) => (
                  <tr key={record.id}>
                    <td>{formatDate(record.expirationDate)}</td>
                    <td>{formatCurrency(record.feeAmount)}</td>
                    <td>
                      {record.isPaid ? formatDate(record.paidDate) : "Not paid"}
                      <small>{formatCurrency(record.paidAmount)}</small>
                    </td>
                    <td>{paymentMethodLabel(record.paymentMethod)}</td>
                    <td>{record.confirmationNumber || "-"}</td>
                    <td>
                      <em className={`pill ${statusTone(record)}`}>
                        {statusLabel(record)}
                      </em>
                    </td>
                    <td>
                      <div className="medication-row-actions">
                        <Tooltip title="Details">
                          <button
                            className="resident-icon-button"
                            type="button"
                            aria-label="View license record"
                            onClick={() => openView(record)}
                          >
                            <i className="ti ti-eye" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <button
                            className="resident-icon-button"
                            type="button"
                            aria-label="Edit license record"
                            onClick={() => openEdit(record)}
                          >
                            <i className="ti ti-edit" />
                          </button>
                        </Tooltip>
                        <Popconfirm
                          title="Delete license record?"
                          description="This record will be removed from the history."
                          okText="Delete"
                          okButtonProps={{
                            danger: true,
                            loading: deletingId === record.id,
                          }}
                          onConfirm={() => deleteRecord(record)}
                        >
                          <Tooltip title="Delete">
                            <button
                              className="resident-icon-button danger"
                              type="button"
                              aria-label="Delete license record"
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </Tooltip>
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <GenericEvidenceForm
        open={drawerOpen}
        evidenceType={EVIDENCE_TYPE}
        workspaceId={selectedWorkspaceId}
        actorId={actorId}
        record={drawerMode === "CREATE" ? null : activeRecord}
        readOnly={drawerMode === "VIEW"}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
      />
    </CareShell>
  );
}
